import { Button, ButtonGroup, Chip } from '@mui/material';
import { GetRowIdParams, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useRef } from 'react';
import { CompanyModel, MarketViewUserModel } from '../../../../Generated/Mark-It-View-Api';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXTextField } from '../../../StoneXMui';

interface MarketViewUsersTableProps {
  users: MarketViewUserModel[];
  companies: CompanyModel[];
  showFilter?: boolean;
  showCompanyColumn?: boolean;
  showActions?: boolean;
  onUpdateUser?: (user: MarketViewUserModel) => void;
  onToggleUserEnabled?: (user: MarketViewUserModel) => void;
}

export function MarketViewUsersTable(props: MarketViewUsersTableProps) {
  const {
    users,
    companies,
    showFilter = true,
    showCompanyColumn = true,
    showActions = false,
    onUpdateUser,
    onToggleUserEnabled,
  } = props;

  const gridRef = useRef<AgGridReact<MarketViewUserModel>>(null);

  function onFilterUpdate(filterText: string) {
    gridRef.current!.api.setGridOption('quickFilterText', filterText);
  }

  const maximumChips = 3;

  const columnDefs = [
    {
      headerName: 'First Name',
      field: 'firstName',
      sortable: true,
      flex: 1,
    },
    {
      headerName: 'Last Name',
      field: 'lastName',
      sortable: true,
      flex: 1,
    },
    {
      headerName: 'Company',
      field: 'companyGuid',
      sortable: true,
      flex: 1,
      hide: !showCompanyColumn,
      valueFormatter: (params: ValueFormatterParams<MarketViewUserModel>) => {
        const company = companies.find((companyRow) => companyRow.companyGuid === params.value);
        return company?.name ?? params.data?.company?.name ?? '';
      },
    },
    {
      headerName: 'Email',
      field: 'email',
      sortable: true,
      flex: 1,
    },
    {
      headerName: 'Last Seen',
      field: 'lastAuthorizationTime',
      sortable: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams<MarketViewUserModel>) =>
        params.value ? dayjs.utc(params.value).local().format('L LT') : '',
      filter: 'agDateColumnFilter',
    },
    {
      headerName: 'Applications',
      field: 'company',
      sortable: true,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<MarketViewUserModel>) => {
        const applications = params.data?.company?.marketViewApplications as string[] | undefined;

        if (!applications || applications.length === 0) {
          return null;
        }

        const displayedApplications = applications.slice(0, maximumChips);
        const remainingApplicationCount = applications.length - maximumChips;

        return (
          <>
            {displayedApplications.map((application) => (
              <Chip key={application} label={application} size="small" style={{ marginRight: 4 }} />
            ))}
            {applications.length > maximumChips && (
              <Chip
                key="more"
                label={`+${remainingApplicationCount}`}
                size="small"
                style={{ marginRight: 4, background: '#eee', fontWeight: 600 }}
              />
            )}
          </>
        );
      },
    },
    {
      headerName: 'Sites',
      field: 'commodityManagementUserSiteAccesses',
      sortable: true,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<MarketViewUserModel>) => {
        const sites = params.value as { siteGuid: string; siteName: string }[] | undefined;

        if (!sites || sites.length === 0) {
          return null;
        }

        const displayedSites = sites.slice(0, maximumChips);
        const remainingSiteCount = sites.length - maximumChips;

        return (
          <>
            {displayedSites.map((site) => (
              <Chip
                key={site.siteName}
                label={site.siteName}
                size="small"
                style={{
                  marginRight: 4,
                  fontWeight: params.data?.commodityManagementUserSiteAccesses?.find(
                    (siteAccess) => siteAccess.siteGuid === site.siteGuid,
                  )?.isSiteManager
                    ? 700
                    : undefined,
                }}
              />
            ))}
            {sites.length > maximumChips && (
              <Chip
                key="more"
                label={`+${remainingSiteCount}`}
                size="small"
                style={{ marginRight: 4, background: '#eee', fontWeight: 600 }}
              />
            )}
          </>
        );
      },
    },
    {
      headerName: 'Admin',
      field: 'isAdmin',
      sortable: true,
      width: 100,
      cellRenderer: (params: ICellRendererParams<MarketViewUserModel>) =>
        params.data?.isAdministrator ? <Chip variant="success" label="ADMIN" /> : <></>,
    },
    {
      headerName: 'Status',
      field: 'isDisabled',
      sortable: true,
      width: 180,
      cellRenderer: (params: ICellRendererParams<MarketViewUserModel>) => {
        if (params.data?.company?.isDisabled) {
          return <Chip variant="alert" label="COMPANY DISABLED" />;
        }

        if (params.data?.isDisabled) {
          return <Chip variant="info" label="DISABLED" />;
        }

        return <></>;
      },
    },
    {
      headerName: 'Date Added',
      field: 'userAddedDate',
      sortable: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams<MarketViewUserModel>) =>
        params.value ? dayjs.utc(params.value).local().format('L LT') : '',
      filter: 'agDateColumnFilter',
    },
    {
      headerName: 'Added By',
      field: 'addedByUserName',
      sortable: true,
      flex: 1,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Actions',
      field: 'actions',
      width: 200,
      hide: !showActions,
      cellRenderer: (params: ICellRendererParams<MarketViewUserModel>) => (
        <ButtonGroup variant="text">
          <Button onClick={() => onUpdateUser?.(params.data!)}>Update</Button>
          <Button onClick={() => onToggleUserEnabled?.(params.data!)}>{params.data?.isDisabled ? 'Enable' : 'Disable'}</Button>
        </ButtonGroup>
      ),
    },
  ];

  return (
    <>
      {showFilter && (
        <StoneXRow>
          <StoneXTextField placeholder="Filter..." onUpdate={onFilterUpdate} />
        </StoneXRow>
      )}

      <StoneXRow align="end">
        <div style={{ width: '100%' }}>
          <StoneXGrid
            ref={gridRef}
            getRowId={(params: GetRowIdParams) => params.data.marketViewUserGuid}
            columns={columnDefs}
            rows={users}
            gridOptions={{ pagination: true, paginationPageSize: 50 }}
            hideGridOptions
          />
        </div>
      </StoneXRow>
    </>
  );
}
